import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { atomWithLocalStorage } from "common/storage";

/**
 * How the location on screen was chosen. Home's city row shows a second line
 * for two of these and nothing for the third:
 *
 * - `city` — picked from a list or a search. The city name is the whole fact,
 *   so there is nothing to put under it.
 * - `map` — a point dropped on the map. The city alone does not say *where*,
 *   so the resolved street address goes under it.
 * - `gps` — following the device. The bold line names the city we are standing
 *   in, and "Current location" under it is what says we are tracking rather
 *   than parked on a city someone picked.
 *
 * Native only ever shows the `gps` second line (@see `HomeTab`'s `citySubtitle`)
 * and deliberately drops the address behind a map point. Desktop keeps it.
 */
export type ManualLocationSource = "city" | "map" | "gps";

/** Where the user has pointed the app, as the home page's params want it. */
export type ManualLocation = {
  latitude: string;
  longitude: string;
  /** Short name for the header — "Paris", not "Paris, Île-de-France, France". */
  label: string;
  /**
   * Optional so a record written before this existed still reads. Absent is
   * treated as `city`, which is the case that shows no second line — the
   * quietest thing a stale record can do.
   */
  source?: ManualLocationSource;
  /**
   * The full formatted address, for `map` only. Kept beside the label rather
   * than replacing it, because the bold line stays a city either way.
   */
  address?: string;
};

/**
 * The location the user chose, persisted.
 *
 * Desktop's answer to native's `setManualLocation`, and it exists for the same
 * reason: there has to be **one** durable record of where the app is pointed,
 * or every source of a location ends up replaying its own idea of it.
 *
 * Before this, the home page's coordinates lived only in the in-memory
 * `desktopHomePageSearchParamsAtom` and onboarding's chosen city was re-derived
 * from the stored answers on every mount. A location picked from the header
 * therefore lost to onboarding's answer twice over: on a reload, because the
 * header's change was never persisted, and on any remount of the home page
 * (`/` → `/search` → `/`), because re-deriving had nothing to tell it the user
 * had since moved on.
 *
 * Now both write here and the last write wins, whoever made it. That also makes
 * re-applying on mount the *correct* behaviour rather than a bug: what gets
 * replayed is the user's current choice, not a stale answer.
 *
 * `null` means the user has never chosen — home falls back to geolocation, then
 * to `defaultRegion` (London), exactly as it did before.
 */
export const manualLocationAtom = atomWithLocalStorage<ManualLocation | null>(
  "desktopManualLocation",
  null,
);

/**
 * Read and set the chosen location.
 *
 * Deliberately **not** called from `useOnboardingFlow`: the flow hook is
 * mounted by every onboarding step, and only the city step has any business
 * setting a location. Native draws the same line — its `setManualLocation` is
 * called from `CityScreen`, not the flow hook.
 */
export function useManualLocation() {
  const manualLocation = useAtomValue(manualLocationAtom);
  const set = useSetAtom(manualLocationAtom);

  const setManualLocation = useCallback(
    (next: ManualLocation) => set(next),
    [set],
  );

  return { manualLocation, setManualLocation };
}
